import { useCallback, useRef } from "react";
import { conversationsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  deliveryErrorDescription,
  gmailDeliveryErrorDescription,
  waitForDraftGeneration,
} from "@/lib/inboxHelpers";

const STATUS_LABELS = {
  open: "reopened",
  closed: "closed",
  resolved: "resolved",
  escalated: "escalated",
};

export function useInboxActions({
  activeConv,
  activeFilter,
  draft,
  composerMessageId,
  composerMessage,
  composerMode,
  isEmailChannel,
  assistantName,
  setBusyAction,
  setRetryId,
  setComposerOpen,
  setComposerMode,
  setComposerMessageId,
  setActiveConv,
  setList,
  setActiveMessages,
  setDraftGenerating,
  loadDetail,
  refreshList,
}) {
  // Guard against rapid double-clicks before React re-renders busy=true.
  const inFlightRef = useRef(null);

  const beginAction = useCallback((key) => {
    if (inFlightRef.current) return false;
    inFlightRef.current = key;
    setBusyAction(key);
    return true;
  }, [setBusyAction]);

  const endAction = useCallback(() => {
    inFlightRef.current = null;
    setBusyAction(null);
  }, [setBusyAction]);

  const clearPendingDraftsLocally = useCallback((conversationId, discardedIds = []) => {
    const idSet = new Set((discardedIds || []).map(String).filter(Boolean));
    let remainingDrafts = 0;
    if (setActiveMessages) {
      setActiveMessages((prev) => {
        const next = prev
          .map((m) => {
            if (m.status !== "draft") return m;
            if (idSet.size === 0 || idSet.has(String(m.id))) {
              return { ...m, status: "discarded" };
            }
            return m;
          })
          .filter((m) => m.status !== "discarded");
        remainingDrafts = next.filter((m) => m.status === "draft").length;
        return next;
      });
    }
    setList((prev) => prev.map((c) => {
      if (String(c.id) !== String(conversationId)) return c;
      return { ...c, has_pending_draft: remainingDrafts > 0 };
    }));
  }, [setActiveMessages, setList]);

  const onStatusChange = useCallback(async (nextStatus) => {
    if (!activeConv || nextStatus === activeConv.status) return;
    if (!beginAction("status")) return;
    try {
      const r = await conversationsApi.updateStatus(activeConv.id, nextStatus);
      const updated = r.conversation;
      setActiveConv(updated);
      setList((prev) => {
        const next = prev.map((c) => (c.id === updated.id ? { ...c, status: updated.status } : c));
        if (activeFilter === "open" && (nextStatus === "closed" || nextStatus === "resolved")) {
          return next.filter((c) => c.id !== updated.id);
        }
        return next;
      });
      await refreshList({ background: true });
      const label = STATUS_LABELS[nextStatus] || nextStatus;
      toast.success(`Conversation ${label}`);
    } catch (e) {
      toast.error(e?.message || "Could not update status");
    } finally {
      endAction();
    }
  }, [activeConv, activeFilter, beginAction, endAction, refreshList, setActiveConv, setList]);

  const reloadAfterAction = useCallback(async () => {
    // Draft approve / discard / regenerate must not drive read-state — only refresh
    // thread + list after the draft lifecycle change.
    await loadDetail(activeConv.id, { silent: true, markRead: false });
    await refreshList();
  }, [activeConv, loadDetail, refreshList]);

  /** Refresh the open thread first so draft UI can paint before list sync. */
  const reloadThreadThenList = useCallback(async () => {
    await loadDetail(activeConv.id, { silent: true, markRead: false });
    // List badge sync can lag; do not block the draft panel on it.
    refreshList({ background: true });
  }, [activeConv, loadDetail, refreshList]);

  const onApprove = useCallback(async () => {
    if (!draft || !activeConv) return false;
    if (!beginAction("approve")) return false;
    try {
      const result = await conversationsApi.approveDraft(activeConv.id, draft.id);
      await reloadAfterAction();
      // Approve & Send is complete only after delivery is confirmed and UI refreshed.
      toast.success(result?.delivery === "sent" ? "Message sent" : "Draft approved");
      return true;
    } catch (e) {
      toast.error(e?.message || "Could not send message", {
        description: gmailDeliveryErrorDescription(e?.code) || deliveryErrorDescription(e?.code),
      });
      await loadDetail(activeConv.id, { silent: true, markRead: false });
      return false;
    } finally {
      endAction();
    }
  }, [activeConv, beginAction, draft, endAction, loadDetail, reloadAfterAction]);

  const onRetryOutbound = useCallback(async (messageId) => {
    if (!activeConv) return false;
    if (inFlightRef.current) return false;
    inFlightRef.current = "retry";
    setRetryId(messageId);
    try {
      await conversationsApi.retryOutbound(activeConv.id, messageId);
      await reloadAfterAction();
      toast.success("Message sent");
      return true;
    } catch (e) {
      toast.error(
        e?.message || "Unable to resend the message at the moment. Please try again in a few moments.",
        { description: deliveryErrorDescription(e?.code) },
      );
      await loadDetail(activeConv.id, { silent: true, markRead: false });
      return false;
    } finally {
      inFlightRef.current = null;
      setRetryId(null);
    }
  }, [activeConv, loadDetail, reloadAfterAction, setRetryId]);

  const onDiscard = useCallback(async () => {
    if (!draft || !activeConv) return false;
    if (!beginAction("discard")) return false;
    try {
      const result = await conversationsApi.discardDraft(activeConv.id, draft.id);
      clearPendingDraftsLocally(activeConv.id, result?.discarded_ids || [draft.id]);
      await reloadAfterAction();
      toast.success("Draft discarded");
      return true;
    } catch (e) {
      toast.error(e?.message || "Could not discard draft");
      await loadDetail(activeConv.id, { silent: true, markRead: false });
      return false;
    } finally {
      endAction();
    }
  }, [activeConv, beginAction, clearPendingDraftsLocally, draft, endAction, loadDetail, reloadAfterAction]);

  const onRegenerateDraft = useCallback(async () => {
    if (!activeConv) return false;
    if (!beginAction("regenerate")) return false;
    setDraftGenerating?.(true);
    toast.info(`${assistantName} is drafting a reply — this can take up to a minute.`);
    try {
      let kickoff = null;
      try {
        kickoff = await conversationsApi.regenerateDraft(activeConv.id);
      } catch (startErr) {
        // Another producer already holds the compose claim (inbound auto-draft).
        // Attach to that job instead of failing with a confusing error toast.
        if (startErr?.code === "draft_generating" || startErr?.status === 409) {
          kickoff = { status: "generating" };
        } else if (startErr?.code === "network_error") {
          // Legacy proxies may still drop long sync calls; recover by polling job/thread.
          toast.message("Connection interrupted — checking for a completed draft…");
        } else {
          throw startErr;
        }
      }
      const result = await waitForDraftGeneration(conversationsApi, activeConv.id, kickoff);
      // Hydrate the thread before clearing the generating banner so the draft
      // panel replaces the loader in the same turn (no empty reply-bar gap).
      await reloadThreadThenList();
      if (result?.status === "idle") {
        toast.error("No draft was generated for this conversation.");
        return false;
      }
      toast.success(`${assistantName} drafted a new reply`);
      return true;
    } catch (e) {
      toast.error(
        e?.message || "Unable to generate an AI draft at the moment. Please try again in a few moments.",
      );
      // Draft may still have landed — refresh silently so the user sees it if so.
      try {
        await reloadThreadThenList();
      } catch {
        /* ignore */
      }
      return false;
    } finally {
      setDraftGenerating?.(false);
      endAction();
    }
  }, [activeConv, assistantName, beginAction, endAction, reloadThreadThenList, setDraftGenerating]);

  const onSendManual = useCallback(async (bodyText) => {
    if (!activeConv || !bodyText.trim()) return false;
    if (!beginAction("send")) return false;
    try {
      const result = await conversationsApi.sendMessage(activeConv.id, bodyText.trim());
      await reloadAfterAction();
      toast.success(result?.delivery === "sent" ? "Message sent" : "Reply sent");
      return true;
    } catch (e) {
      toast.error(e?.message || "Could not send message", {
        description: deliveryErrorDescription(e?.code),
      });
      await loadDetail(activeConv.id, { silent: true });
      return false;
    } finally {
      endAction();
    }
  }, [activeConv, beginAction, endAction, loadDetail, reloadAfterAction]);

  const persistComposerPayload = useCallback(async (payload, { reactivate = false } = {}) => {
    if (!activeConv) return null;
    if (!payload?.body_text?.trim()) {
      toast.error("Draft cannot be empty");
      return null;
    }
    const draftPayload = isEmailChannel
      ? { body_text: payload.body_text, body_html: payload.body_html }
      : payload;
    let messageId = composerMessageId;
    if (!messageId) {
      const created = await conversationsApi.createDraft(activeConv.id, draftPayload);
      messageId = created.message_id;
      setComposerMessageId(messageId);
    } else {
      await conversationsApi.editDraft(activeConv.id, messageId, {
        ...draftPayload,
        reactivate,
      });
    }
    return messageId;
  }, [activeConv, composerMessageId, isEmailChannel, setComposerMessageId]);

  const onComposerSave = useCallback(async (payload) => {
    if (!activeConv) return false;
    if (!beginAction("edit")) return false;
    try {
      const messageId = await persistComposerPayload(payload, { reactivate: true });
      if (!messageId) return false;
      setComposerOpen(false);
      await reloadAfterAction();
      toast.success("Draft saved");
      return true;
    } catch (e) {
      toast.error(e?.message || "Could not save draft");
      return false;
    } finally {
      endAction();
    }
  }, [activeConv, beginAction, endAction, persistComposerPayload, reloadAfterAction, setComposerOpen]);

  const onComposerSend = useCallback(async (payload) => {
    if (!activeConv) return false;
    if (!beginAction("send")) return false;
    try {
      const messageId = await persistComposerPayload(payload, { reactivate: true });
      if (!messageId) return false;
      const result = await conversationsApi.approveDraft(activeConv.id, messageId);
      setComposerOpen(false);
      setComposerMessageId(null);
      await reloadAfterAction();
      toast.success(result?.delivery === "sent" ? "Message sent" : "Draft approved");
      return true;
    } catch (e) {
      toast.error(e?.message || "Could not send message");
      await loadDetail(activeConv.id, { silent: true });
      return false;
    } finally {
      endAction();
    }
  }, [activeConv, beginAction, endAction, loadDetail, persistComposerPayload, reloadAfterAction, setComposerMessageId, setComposerOpen]);

  return {
    onStatusChange,
    onApprove,
    onRetryOutbound,
    onDiscard,
    onRegenerateDraft,
    onSendManual,
    onComposerSave,
    onComposerSend,
  };
}
